import {
  Component,
  OnDestroy,
  OnInit,
  Output,
  EventEmitter,
  HostBinding,
  ChangeDetectionStrategy
} from '@angular/core';
import { Subscription } from 'rxjs';
import { NotificationCounterService } from '@app/shared/services/notification-counter.service';
import { NavNotificationsService } from '@api/api-services/nav-notifications.service';
import { TokenStorageService } from '@app/shared/services/token-storage.service';
import { NotificationsCounterApiModel } from '@api/api-models';
import { LayoutService } from '@shared/services';
import { SubnavState } from '@shared/enums';
import { LiveAnnouncer } from '@angular/cdk/a11y';

@Component({
  selector: 'top-sub-nav',
  templateUrl: './top-sub-nav.component.html',
  styleUrls: ['./top-sub-nav.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class TopSubNavComponent implements OnInit, OnDestroy {
  @HostBinding('class.mobile')
  get isMobile(): boolean {
    return this.layoutService.isMobileScreen;
  }

  @HostBinding('class.disabled')
  public get disabled(): boolean {
    return this.layoutService.barsDisabled;
  }

  public notificationCount = 0;
  public currentProgramName: string;
  public notificationsOpen = false;

  private readonly subs = new Subscription();
  @Output() showSubnav = new EventEmitter<void>();

  constructor(
    private readonly notificationCounterService: NotificationCounterService,
    private readonly notificationsService: NavNotificationsService,
    private readonly tokenStorageService: TokenStorageService,
    private readonly layoutService: LayoutService,
    private readonly liveAnnouncer: LiveAnnouncer
  ) {}

  ngOnInit(): void {
    this.notificationCounterService.startRefreshingNotifications();
    this.currentProgramName = this.tokenStorageService.userInfo?.CurrentProgramName || '';

    this.subs.add(
      this.notificationCounterService.counters$.subscribe((counters: NotificationsCounterApiModel) => {
        this.notificationCount = counters?.UnreadCount || 0;
      })
    );

    this.subs.add(
      this.tokenStorageService.userInfoChanged.subscribe(() => {
        this.currentProgramName = this.tokenStorageService.userInfo?.CurrentProgramName || '';
      })
    );

    this.subs.add(
      this.notificationsService.subscribeNotificationStateActionObserver().subscribe((model) => {
        this.notificationCounterService.handleNotificationStateAction(model);
        this.notificationsOpen = this.notificationsService.isNotificationsOpen;
      })
    );
  }

  ngOnDestroy(): void {
    this.notificationCounterService.stopRefreshingNotifications();
    this.subs.unsubscribe();
  }

  public getCurrentProgramAriaLabel(): string {
    const action = this.layoutService.subnavState === SubnavState.full ? 'collapse' : 'expand';
    return `${this.currentProgramName}. Click to ${action} side navigation menu`;
  }

  public toggleNotifications(): void {
    if (!this.notificationsService.isNotificationsOpen && this.layoutService.isMobileScreen) {
      this.layoutService.subnavState = SubnavState.hidden;
    }
    const newState = !this.notificationsService.isNotificationsOpen;
    this.notificationsService.emitNotificationsStateObserver(newState);
    this.notificationsOpen = newState;
  }

  public onCurrentProgramClick(): void {
    if (this.notificationsService.isNotificationsOpen) {
      this.notificationsService.emitNotificationsStateObserver(false);
    }
    this.showSubnav.emit();
    if (this.layoutService.subnavState === SubnavState.collapsed) {
      this.liveAnnouncer.announce(`Side navigation menu is collapsed`, 'assertive');
    } else if (this.layoutService.subnavState === SubnavState.full) {
      this.liveAnnouncer.announce(`Side navigation menu is expanded`, 'assertive');
    }
  }

  public get subnavHidden(): boolean {
    return this.layoutService.subnavState === 'hidden';
  }
  public get subnavFull(): boolean {
    return this.layoutService.subnavState === 'full';
  }
}
